import {
  Box,
  Button,
  CircularProgress,
  makeStyles,
  Typography,
  Paper,
  Divider,
} from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  apiAcquirePayment,
  apiGetOrganizationDetails,
  apiProjectQuotePrice,
} from "src/api/endpoints";
import _ from "lodash";
import { connect } from "react-redux";
import { useSnackbar } from "notistack";
import AccountBalanceIcon from "@material-ui/icons/AccountBalance";
import PaymentIcon from "@material-ui/icons/Payment";
import CancelIcon from "@material-ui/icons/Cancel";
import CheckIcon from "@material-ui/icons/Check";

const useStyles = makeStyles((theme) => ({
  spinner: {
    position: "absolute",
    top: "48%",
    left: "48%",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(3),
    width: "500px",
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[10],
  },
  root: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: theme.spacing(1),
    height: "100vh",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
  },
  submit: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(2),
  },
  header: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    textAlign: "center",
  },
  balanceBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
    width: "100%",
    justifyContent: "center",
  },
  messageBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.spacing(1),
  },
  button: {
    padding: theme.spacing(1, 3),
    gap: theme.spacing(1),
    display: "flex",
    alignItems: "center",
  },
}));

function ConnectCreditCalculate({
  projectId,
  arrangeSubmitBody,
  formData,
  files,
  projectType,
  fileActionType,
  setPaymentId,
  setPhase,
}) {
  const [quote, setQuote] = useState({});
  const [organization, setOrganization] = useState({});
  const classes = useStyles();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    const submitBody = { ...arrangeSubmitBody() };
    for (const sample of submitBody["samples"]) {
      if (typeof sample.filename === "string") {
        if (projectType === "nanopore" && fileActionType === "attach") {
          delete sample.byte_size;
        } else {
          sample.name = sample.filename;
        }
      }

      if (typeof sample.pair_1 === "string") {
        sample.pair_1 = {
          name: sample.pair_1,
        };
      }

      if (typeof sample.pair_2 === "string") {
        sample.pair_2 = {
          name: sample.pair_2,
        };
      }
    }

    submitBody["sample_kind"] = formData["sample_kind"];
    submitBody["project_type"] = formData["project_type"];
    submitBody["pipeline_class"] = formData["pipeline_class"];
    submitBody["sequencer"] = formData["sequencer"];
    submitBody["kit"] = formData["kit"];

    apiProjectQuotePrice(projectId, submitBody, (data, status) => {
      if (status === 200) {
        setQuote(data);
      } else if (status === 409) {
        enqueueSnackbar(
          t("Selected kit is wrong, please contact to Massive admins"),
          {
            variant: "error",
          }
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    apiGetOrganizationDetails(null, (data, status) => {
      if (status === 200) {
        setOrganization(data);
      }
    });
  }, []);

  const onSubmitClick = () => {
    if (organization.credit_balance - quote.final_price < -100) {
      enqueueSnackbar(
        t("Credit is not enough, please contact to Massive admins"),
        {
          variant: "error",
        }
      );
      return;
    }
    const body = {};

    body["project_id"] = projectId;
    body["quote_id"] = quote.quote_id;

    apiAcquirePayment(body, (data, status) => {
      if (status === 200) {
        setPaymentId(data.payment_id);
        setPhase("upload");
      }
    });
  };

  const onCancelClick = () => {
    setPhase("init");
  };

  if (_.isEmpty(quote) && _.isEmpty(organization)) {
    return <CircularProgress className={classes.spinner} />;
  } else
    return (
      <Box className={classes.root}>
        <Paper className={classes.paper} elevation={3}>
          <Box className={classes.header}>
            <Typography
              variant="h4"
              color="primary"
              style={{ fontWeight: "bold" }}
            >
              {t("Credit Quote") + "!"}
            </Typography>
            <Box className={classes.balanceBox}>
              <AccountBalanceIcon color="primary" />
              <Typography variant="h6">
                {`${t("Organization Balance")} ${organization.credit_balance}`}
              </Typography>
            </Box>
          </Box>

          <Divider />

          <Box className={classes.messageBox}>
            <PaymentIcon color="primary" />
            <Typography variant="body1">
              {t(
                "For this project, {{credit}} credits will be reserved as a provisional hold. Once the project is successfully completed, the amount will be deducted from your available credits.",
                {
                  credit: quote.final_price,
                }
              )}
            </Typography>
          </Box>

          <Box className={classes.submit}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={onCancelClick}
              className={classes.button}
              startIcon={<CancelIcon />}
            >
              {t("Cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={onSubmitClick}
              className={classes.button}
              startIcon={<CheckIcon />}
            >
              {t("Submit")}
            </Button>
          </Box>
        </Paper>
      </Box>
    );
}

const mapStateToProps = (state) => {
  return {
    appConf: state.appConf,
    userObj: state.userObject,
  };
};

const CreditCalculate = connect(mapStateToProps)(ConnectCreditCalculate);

export default CreditCalculate;
